import React from 'react';
import useDialogFocus from '../../../../hooks/useDialogFocus';

const COPY = {
  starting: {
    title: 'Starting…',
    body: null,
  },
  submitting: {
    title: 'Saving your responses…',
    body: null,
  },
  finalizing: {
    title: 'Scoring your run…',
    body: null,
  },
  paused: {
    title: 'Paused',
    body: 'You switched away, so we stopped the clock. Pick up where you left off.',
  },
  submitFailed: {
    title: "Couldn't save that block",
    body: "Your responses are still here. We'll send exactly the same ones again.",
  },
  error: {
    title: 'This run had to stop',
    body: 'Nothing you did caused this. Your earlier blocks were saved.',
  },
};

// A request is in flight in these phases. Offering "Abandon" mid-flight invites
// the user to leave while the server is still deciding what happened to the run.
const IN_FLIGHT_PHASES = ['starting', 'submitting', 'finalizing'];

// Escape resolves to the panel's SAFE way out, never to Abandon: a paused run
// is still finishable, and a stray Escape must not throw it away. Phases in
// IN_FLIGHT_PHASES get no binding at all, for the same reason they render no
// exit control — there is no safe exit while the server is still deciding what
// happened to the run. `error` is the one phase where leaving is safe: the run
// has already stopped, and leaving is the only action on offer.
function safeEscapeAction(phase, { onResume, onExit }) {
  if (IN_FLIGHT_PHASES.includes(phase)) return null;
  if (phase === 'paused') return onResume;
  if (phase === 'error') return onExit;
  // submitFailed deliberately has none: Retry and Abandon are a real choice,
  // and Escape must not pick either one on the participant's behalf.
  return null;
}

export default function InterruptPanel({ phase, error, onRetry, onResume, onExit }) {
  const copy = COPY[phase];
  // This panel covers the runner chrome opaquely, so without a focus trap Tab
  // reaches the shell's Exit button behind it — an invisible control the
  // participant activates by accident instead of the visible Resume/Retry.
  const dialogRef = useDialogFocus({
    active: Boolean(copy),
    onEscape: copy ? safeEscapeAction(phase, { onResume, onExit }) : null,
  });
  if (!copy) return null;

  // Log for developers but never render raw backend error messages — they can
  // carry internal detail (session IDs, ownership assertions) that is not
  // user-actionable and creates an information-leak surface.
  if (error && process.env.NODE_ENV !== 'production') {
    console.error('[InterruptPanel] runner error:', error);
  }

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="interrupt-panel-title"
      // The panel holds focus itself when it has no buttons to offer (the
      // in-flight phases), so the title is still announced.
      tabIndex={-1}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg-primary)',
        padding: 'var(--space-5)',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 380 }}>
        <h2
          id="interrupt-panel-title"
          style={{
            margin: '0 0 var(--space-3)',
            fontSize: 'var(--text-section-size)',
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 'var(--text-section-tracking)',
          }}
        >
          {copy.title}
        </h2>
        {copy.body && (
          <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-body-size)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {copy.body}
          </p>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center', marginTop: 'var(--space-4)', flexWrap: 'wrap' }}>
          {phase === 'paused' && (
            <PrimaryButton onClick={onResume}>Resume</PrimaryButton>
          )}
          {phase === 'submitFailed' && <PrimaryButton onClick={onRetry}>Retry</PrimaryButton>}
          {!IN_FLIGHT_PHASES.includes(phase) && (
            <button
              type="button"
              onClick={onExit}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-subtle)',
                background: 'transparent',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-body-size)',
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {phase === 'error' ? 'Back to G-Score' : 'Abandon'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PrimaryButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: 'var(--space-3) var(--space-5)',
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'var(--btn-primary-bg)',
        color: 'var(--btn-primary-text)',
        fontSize: 'var(--text-body-size)',
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
